/**
 * VerifyEmailComp — public, no auth required (see mainRouter.js).
 *
 * URL:   /verify-email?token={verificationToken}
 * Flow:
 *   1. Extract ?token= from URL
 *   2. GET /auth/VerifyEmailLink?token=...  →  { Email, AccountCodes, RequiresPassword }
 *   3. Caller sets a password
 *   4. POST /auth/CompleteEmailVerification  →  { Token, Password }
 *   5. Success — link to /login (this doesn't log the user in automatically)
 *
 * See AltrackWebApi documentation/email-login.md for the full feature design.
 */
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, TextField,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "../../api/axios";

export default function VerifyEmailComp() {
  const [phase, setPhase] = useState("loading");    // loading | ready | submitting | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [accountCodes, setAccountCodes] = useState([]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldError, setFieldError] = useState("");

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) {
      setErrorMsg("No verification token in the URL. Use the link from your email, or ask help desk to resend it.");
      setPhase("error");
      return;
    }
    axios
      .get("/auth/VerifyEmailLink", { params: { token } })
      .then(({ data }) => {
        setEmail(data.email ?? data.Email ?? "");
        setAccountCodes(data.accountCodes ?? data.AccountCodes ?? []);
        setPhase("ready");
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "This link is invalid or has expired. Ask help desk to resend it.";
        setErrorMsg(msg);
        setPhase("error");
      });
  }, [token]);

  const submit = () => {
    setFieldError("");
    if (password.length < 6) {
      setFieldError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setFieldError("Passwords don't match.");
      return;
    }

    setPhase("submitting");
    axios
      .post("/auth/CompleteEmailVerification", { Token: token, Password: password })
      .then(() => {
        setPhase("done");
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Could not verify — the link may have expired. Ask help desk to resend it.";
        setErrorMsg(msg);
        setPhase("error");
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
        p: 3,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "420px",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          p: { xs: 4, md: 5 },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <img src={"/images/logo.png"} alt="Altrack" style={{ height: "48px" }} />
        </Box>

        {phase === "loading" && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {phase === "error" && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
            <Link component={RouterLink} to="/login" sx={{ fontSize: "14px" }}>Back to login</Link>
          </>
        )}

        {phase === "done" && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Verified. You can now log in with your email and password.
            </Alert>
            {window.innerWidth < 768 ? (
              // The logistics webapp isn't usable on mobile — don't send a mobile visitor
              // into a "Go to login" dead end.
              <Box sx={{ fontSize: "14px", color: "#6b7280" }}>
                Your account is ready. Please log in from a desktop or laptop computer — the
                Altrack Logistics app isn't designed for mobile devices.
              </Box>
            ) : (
              <Button component={RouterLink} to="/login" variant="contained" fullWidth
                sx={{ backgroundColor: "#1a1a1a", "&:hover": { backgroundColor: "#333" } }}>
                Go to login
              </Button>
            )}
          </>
        )}

        {(phase === "ready" || phase === "submitting") && (
          <>
            <Box sx={{ fontSize: "22px", fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}>
              Set your password
            </Box>
            <Box sx={{ fontSize: "14px", color: "#6b7280", mb: 3 }}>
              Confirming <b>{email}</b>
              {accountCodes.length > 0 && (
                <> for account{accountCodes.length > 1 ? "s" : ""} {accountCodes.join(", ")}</>
              )}
            </Box>

            {fieldError && <Alert severity="error" sx={{ mb: 2 }}>{fieldError}</Alert>}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={phase === "submitting"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        <FontAwesomeIcon icon={showPassword ? "eye-slash" : "eye"} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={phase === "submitting"}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={submit}
                disabled={phase === "submitting"}
                sx={{ backgroundColor: "#1a1a1a", "&:hover": { backgroundColor: "#333" }, mt: 1 }}
              >
                {phase === "submitting" ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Set password"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
